import {
  createTransactionData,
  decodeParameter,
  decodeParameters,
} from "../library/Web3Module";
import { nft_address, token_address } from "../const/contract";
import { NFTRepository } from "../repository/NFTRepository";

/**
 * NFTController 클래스는 NFT 관련 기능을 제공합니다.
 */
class NFTController {
  static instance = null;

  /**
   * NFTController의 싱글톤 인스턴스를 반환합니다.
   * @returns {NFTController} NFTController 인스턴스
   */
  static getInstance() {
    if (!NFTController.instance) {
      NFTController.instance = new NFTController();
    }
    return NFTController.instance;
  }

  /**
   * NFTController 생성자
   */
  constructor() {
    if (NFTController.instance) {
      return NFTController.instance;
    }
    NFTController.instance = this;
  }

  /**
   * Allowance 설정을 위한 트랜잭션 데이터를 생성합니다.
   * @param {Object} params - Allowance 설정 파라미터
   * @param {string|number} params.amount - 설정할 allowance 양 (USDT 단위)
   * @returns {Object} 트랜잭션 데이터
   */
  createSetAllowanceData(params) {
    try {
      const { amount } = params;

      // USDT decimals는 6이므로, 입력받은 USDT 값을 wei 단위로 변환
      const amountInWei = BigInt(
        Math.floor(parseFloat(amount) * 1000000)
      ).toString();

      // approve 함수의 ABI 문자열
      const abiString =
        "function approve(address spender, uint256 amount) returns (bool)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "approve", [
        nft_address,
        amountInWei,
      ]);

      return {
        to: token_address,
        data,
      };
    } catch (err) {
      console.error("Error creating allowance data:", err);
      throw err;
    }
  }

  /**
   * NFT 민팅을 위한 트랜잭션 데이터를 생성합니다.
   * @param {Object} params - 민팅 파라미터
   * @param {string} params.to - 민팅 대상 주소
   * @param {number} params.tier - NFT 티어
   * @returns {Object} 트랜잭션 데이터
   */
  createMintNFTData(params) {
    try {
      const { to, tier } = params;

      // mint 함수의 ABI 문자열
      const abiString =
        "function mint(address to, uint8 tier) returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "mint", [to, tier]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating mint data:", err);
      throw err;
    }
  }

  /**
   * NFT 민팅을 위한 트랜잭션 데이터를 생성합니다.
   * @param {string} to - 민팅 대상 주소
   * @param {number} tierId - NFT 티어 ID
   * @param {number} imageId - NFT image ID
   * @returns {Object} 트랜잭션 데이터
   */
  createBuyNFTData(to, tierId, imageId) {
    try {
      // const { to, tierId, imageId } = params;

      // mint 함수의 ABI 문자열
      const abiString =
        "function buyNFT(address to, uint8 tier, uint256 paymentId)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "buyNFT", [
        to,
        tierId,
        imageId,
      ]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating buy NFT data:", err);
      throw err;
    }
  }

  /**
   * NFT 정보 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @param {string} tokenId - 토큰 ID
   * @returns {Object} 트랜잭션 데이터
   */
  createGetNFTInfoData(tokenId) {
    try {
      // tokenURI 함수의 ABI 문자열
      const abiString =
        "function tokenURI(uint256 tokenId) returns (string memory)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "tokenURI", [tokenId]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating NFT info data:", err);
      throw err;
    }
  }

  /**
   * NFT 이미지 등록을 위한 트랜잭션 데이터를 생성합니다.
   * @param {string} tokenId - 토큰 ID
   * @param {string} tokenURI - 토큰 URI
   * @returns {Object} 트랜잭션 데이터
   */
  createRegisterNFTImageData(tokenId, tokenURI) {
    try {
      // setTokenURI 함수의 ABI 문자열
      const abiString =
        "function setTokenURI(uint256 tokenId, string memory _tokenURI)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "setTokenURI", [
        tokenId,
        tokenURI,
      ]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating register NFT image data:", err);
      throw err;
    }
  }

  /**
   * Allowance 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @param {Object} params - Allowance 조회 파라미터
   * @param {string} params.ownerAddress - 소유자 주소
   * @returns {Object} 트랜잭션 데이터
   */
  createGetAllowanceData(params) {
    try {
      const { ownerAddress } = params;

      // allowance 함수의 ABI 문자열
      const abiString =
        "function allowance(address owner, address spender) returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "allowance", [
        ownerAddress,
        nft_address,
      ]);

      return {
        to: token_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get allowance data:", err);
      throw err;
    }
  }

  /**
   * 판매 상태 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetSaleStateData() {
    try {
      // saleState 함수의 ABI 문자열
      const abiString = "function saleState() returns (uint8)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "saleState", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get sale state data:", err);
      throw err;
    }
  }

  /**
   * 티어 가격 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @param {number} tier - 조회할 티어
   * @returns {Object} 트랜잭션 데이터
   */
  createGetTierPriceData(tier) {
    try {
      // getTierPrice 함수의 ABI 문자열
      const abiString = "function getTierPrice(uint8 tier) returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "getTierPrice", [tier]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get tier price data:", err);
      throw err;
    }
  }

  /**
   * Aether(tier 0) 최대 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetAetherMaxSupplyData() {
    try {
      // MAX_AETHER_SUPPLY 함수의 ABI 문자열
      const abiString = "function MAX_AETHER_SUPPLY() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "MAX_AETHER_SUPPLY", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Aether max supply data:", err);
      throw err;
    }
  }

  /**
   * Nova(tier 1) 최대 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetNovaMaxSupplyData() {
    try {
      // MAX_NOVA_SUPPLY 함수의 ABI 문자열
      const abiString = "function MAX_NOVA_SUPPLY() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "MAX_NOVA_SUPPLY", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Nova max supply data:", err);
      throw err;
    }
  }

  /**
   * Comet(tier 2) 최대 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetCometMaxSupplyData() {
    try {
      // MAX_COMET_SUPPLY 함수의 ABI 문자열
      const abiString = "function MAX_COMET_SUPPLY() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "MAX_COMET_SUPPLY", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Comet max supply data:", err);
      throw err;
    }
  }

  /**
   * Stardust(tier 3) 최대 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetStardustMaxSupplyData() {
    try {
      // MAX_STARDUST_SUPPLY 함수의 ABI 문자열
      const abiString = "function MAX_STARDUST_SUPPLY() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "MAX_STARDUST_SUPPLY", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Stardust max supply data:", err);
      throw err;
    }
  }

  /**
   * Aether(tier 0) 현재 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetAetherSupplyData() {
    try {
      // aetherSupply 함수의 ABI 문자열
      const abiString = "function aetherSupply() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "aetherSupply", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Aether supply data:", err);
      throw err;
    }
  }

  /**
   * Nova(tier 1) 현재 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetNovaSupplyData() {
    try {
      // novaSupply 함수의 ABI 문자열
      const abiString = "function novaSupply() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "novaSupply", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Nova supply data:", err);
      throw err;
    }
  }

  /**
   * Comet(tier 2) 현재 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetCometSupplyData() {
    try {
      // cometSupply 함수의 ABI 문자열
      const abiString = "function cometSupply() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "cometSupply", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Comet supply data:", err);
      throw err;
    }
  }

  /**
   * Stardust(tier 3) 현재 발행량 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @returns {Object} 트랜잭션 데이터
   */
  createGetStardustSupplyData() {
    try {
      // stardustSupply 함수의 ABI 문자열
      const abiString = "function stardustSupply() returns (uint256)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "stardustSupply", []);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get Stardust supply data:", err);
      throw err;
    }
  }

  /**
   * Allowance 충분 여부를 확인합니다.
   * @param {Object} params - Allowance 확인 파라미터
   * @param {string} params.amount - 확인할 금액
   * @param {string} params.allowance - 현재 allowance
   * @returns {boolean} Allowance 충분 여부
   */
  checkAllowance(params) {
    try {
      const { amount, allowance } = params;

      // BigInt로 변환하여 비교
      const amountBigInt = BigInt(amount);
      const allowanceBigInt = BigInt(allowance);

      return allowanceBigInt >= amountBigInt;
    } catch (err) {
      console.error("Error checking allowance:", err);
      throw err;
    }
  }

  /**
   * 특정 티어의 민팅 가능 여부를 확인합니다.
   * @param {Object} params - 파라미터 객체
   * @param {string} params.userMaxSupply - 사용자 전용 최대 발행량
   * @param {string} params.currentSupply - 현재 발행량
   * @returns {boolean} - 민팅 가능 여부
   */
  isTierMintable(params) {
    try {
      const { userMaxSupply, currentSupply } = params;

      // 값이 없으면 false 반환
      if (!userMaxSupply || !currentSupply) {
        return false;
      }

      // BigInt로 변환하여 계산
      const userMax = BigInt(userMaxSupply);
      const current = BigInt(currentSupply);

      // 사용자 남은 발행 가능량이 0보다 크면 민팅 가능
      return userMax - current > 0n;
    } catch (error) {
      console.error("Error checking tier mintability:", error);
      return false;
    }
  }

  /**
   * 사용자가 보유한 NFT 정보를 조회합니다.
   * @param {string} address - NFT 소유자의 지갑 주소
   * @returns {Promise<Object>} - NFT 정보 객체
   */
  async getNFT(address) {
    try {
      if (!address) {
        throw new Error("지갑 주소가 필요합니다.");
      }

      const nftRepository = new NFTRepository();
      const nftInfos = await nftRepository.findByAddress(address);

      return nftInfos.map((nftInfo) => {
        return {
          ...nftInfo,
          image_metadata:
            JSON.parse(nftInfo.image_metadata) ?? nftInfo.image_metadata,
        };
      });
    } catch (error) {
      console.error("NFT 정보 조회 오류:", error);
      throw error;
    }
  }

  /**
   * NFT 이미지를 설정합니다.
   * @param {Object} params - NFT 이미지 설정 파라미터
   * @param {string} params.address - NFT 소유자의 지갑 주소
   * @param {number} params.nftInfoId - NFT 정보 ID
   * @param {number} params.tierId - NFT 티어 ID
   * @param {Object} params.metadata - 설정할 메타데이터
   * @returns {Promise<Object>} - 업데이트된 NFT 정보
   */
  async setNFTImage(address, nftInfoId, tierId, metadata) {
    try {
      if (!address) {
        throw new Error("지갑 주소가 필요합니다.");
      }

      if (!nftInfoId) {
        throw new Error("NFT Info ID가 필요합니다.");
      }

      if (!metadata || !metadata.image_id) {
        throw new Error("메타데이터 정보가 필요합니다.");
      }

      // 티어 2 이상은 이미지 설정 불가
      if (tierId >= 2) {
        throw new Error("티어 2 이상의 NFT는 이미지를 설정할 수 없습니다.");
      }

      const nftRepository = new NFTRepository();
      const updatedNFT = await nftRepository.updateImage(
        address,
        nftInfoId,
        tierId,
        metadata.image_id
      );

      return {
        ...updatedNFT,
        image_metadata: JSON.parse(updatedNFT.image_metadata),
      };
    } catch (error) {
      console.error("NFT 이미지 설정 오류:", error);
      throw error;
    }
  }

  /**
   * NFT 토큰 URI 조회를 위한 트랜잭션 데이터를 생성합니다.
   * @param {string} tokenId - NFT 토큰 ID
   * @returns {Object} 트랜잭션 데이터
   */
  createGetTokenURIData(tokenId) {
    try {
      if (!tokenId) {
        throw new Error("토큰 ID가 필요합니다.");
      }

      // tokenURI 함수의 ABI 문자열
      const abiString = "function tokenURI(uint256 tokenId) returns (string)";

      // 트랜잭션 데이터 생성
      const data = createTransactionData(abiString, "tokenURI", [tokenId]);

      return {
        to: nft_address,
        data,
      };
    } catch (err) {
      console.error("Error creating get token URI data:", err);
      throw err;
    }
  }

  /**
   * 토큰 URI를 디코딩합니다.
   * @param {string} data - 디코딩할 트랜잭션 데이터
   * @returns {string} 디코딩된 토큰 URI
   */
  decodeTokenURI(data) {
    try {
      if (!data) {
        throw new Error("트랜잭션 데이터가 필요합니다.");
      }

      // tokenURI 파라미터의 ABI 문자열
      const abiString = [{ type: "string", name: "" }];

      // 트랜잭션 데이터 디코딩
      const decodedData = decodeParameters(abiString, data);
      return decodedData[0];
    } catch (err) {
      console.error("Error decoding token URI:", err);
      throw err;
    }
  }

  /**
   * NFT 토큰 메타데이터를 가져옵니다.
   * @param {string} metadataUrl - 메타데이터가 저장된 S3 URL
   * @returns {Promise<Object>} 메타데이터 JSON 객체
   */
  async getTokenMetadata(metadataUrl) {
    try {
      if (!metadataUrl) {
        throw new Error("메타데이터 URL이 필요합니다.");
      }

      // URL이 유효한지 확인
      if (
        !metadataUrl.startsWith("http://") &&
        !metadataUrl.startsWith("https://")
      ) {
        throw new Error("유효하지 않은 URL 형식입니다.");
      }

      // 메타데이터 JSON 파일 가져오기
      const response = await fetch(metadataUrl);

      // 응답 상태 확인
      if (!response.ok) {
        throw new Error(
          `메타데이터를 가져오는데 실패했습니다. 상태 코드: ${response.status}`
        );
      }

      // JSON 파싱
      const metadata = await response.json();

      return metadata;
    } catch (err) {
      console.error("Error fetching token metadata:", err);
      throw err;
    }
  }
}

export { NFTController };
